import React, { useState, useEffect } from 'react';
import { useTickerLink } from '../contexts/TickerLinkContext';
import { LinkIcon } from './LinkIcon';

export const GlobalTickerInput: React.FC = () => {
  const { linkedTicker, linkedWindowIds, setLinkedTicker, globalInputRef, skipNextClear } = useTickerLink();
  const [tickerInput, setTickerInput] = useState(linkedTicker || '');

  // Sync input with linked ticker when it changes externally
  useEffect(() => {
    if (linkedTicker) {
      setTickerInput(linkedTicker);
    }
  }, [linkedTicker]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (tickerInput.trim()) {
      setLinkedTicker(tickerInput.trim().toUpperCase());
      globalInputRef.current?.blur();
    }
  };

  const handleClear = () => {
    setTickerInput('');
    globalInputRef.current?.focus();
  };

  const hasLinkedWidgets = linkedWindowIds.length > 0;

  return (
    <div className="relative flex items-center gap-1" data-tour="global-ticker">
      <form onSubmit={handleSubmit} className="flex items-center">
        <div className="relative">
          <input
            ref={globalInputRef}
            type="text"
            value={tickerInput}
            onChange={(e) => {
              setTickerInput(e.target.value.toUpperCase());
            }}
            onFocus={() => {
              // Don't clear if focus was triggered by keyboard shortcut
              if (skipNextClear.current) {
                skipNextClear.current = false;
              } else {
                setTickerInput('');
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                globalInputRef.current?.blur();
              }
            }}
            placeholder="Ticker"
            title="Type a ticker here to push it to every linked widget"
            className={`w-24 pl-6 pr-5 py-1 text-xs bg-dark-bg border rounded text-accent-yellow font-semibold tracking-wide placeholder:font-normal placeholder:tracking-normal placeholder-gray-500 focus:outline-none transition-colors ${
              hasLinkedWidgets
                ? 'border-accent-yellow/50 focus:border-accent-yellow'
                : 'border-dark-border focus:border-accent-yellow'
            }`}
          />

          {/* ⚠️ Always shown, not only while widgets are linked — it is what
              tells a reader the box is for syncing rather than searching.
              Hidden until something links, the control only explains itself
              once you already know what it does. Gold when live, muted when
              nothing is following. */}
          <span
            className={`pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 transition-colors ${
              hasLinkedWidgets ? 'text-accent-yellow' : 'text-gray-500'
            }`}
          >
            <LinkIcon linked={hasLinkedWidgets} className="w-3 h-3" />
          </span>
          {tickerInput && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white text-xs"
            >
              ×
            </button>
          )}
        </div>
      </form>

      {/* Linked widgets indicator.
          Carries the same chain icon as the per-widget toggle, so the count
          reads as "this many panes are following this box" rather than as an
          unexplained number. Gold, matching every other active state. */}
      {hasLinkedWidgets && (
        <span
          className="flex items-center gap-1 text-[10px] text-accent-yellow bg-accent-yellow/10 px-1.5 py-0.5 rounded"
          title={`${linkedWindowIds.length} widget${linkedWindowIds.length > 1 ? 's' : ''} following this ticker`}
        >
          <span className="font-numeric">{linkedWindowIds.length}</span>
          <span className="uppercase tracking-wider">linked</span>
        </span>
      )}
    </div>
  );
};
